import { UnitsService } from './units.service'
import { PositionsService } from './positions.service'
import { Unit } from '../models/unit'
import { Position } from '../models/position'
import { UnitAllData } from '../models/unit-all-data'
import { Observable } from 'rxjs'
import { Injectable } from '@angular/core'
import { first } from 'rxjs/operators'
import { UnitEvent } from '../../cost-module/models/unit-event'
import { Utils } from '../../shared/utils'
import { GeocoderReq } from '../../core/geocoder/models/geocoder-req'
import { GeocoderService } from '../../core/geocoder/geocoder.service'
import * as moment from 'moment-timezone'
import { PositionStatus } from '../models/position-status'
import { UnitsFacade } from '../../stores/units/facades/units.facade'

@Injectable({
  providedIn: 'root',
})
export class DataGrabber {
  utils: Utils

  constructor(
    private _positionService: PositionsService,
    private _unitService: UnitsService,
    private _geocoderService: GeocoderService,
    private _unitsFacade: UnitsFacade,
  ) {
    this.utils = new Utils()
  }

  joinUnitsByIds(unitIds: number[]): Observable<UnitAllData[]> {
    return new Observable(observer => {
      this.getUnitsByIds(unitIds)
        .pipe(first())
        .subscribe(units => {
          this.getPositions(this.getUnitsIds(units))
            .pipe(first())
            .subscribe(positions => {
              observer.next(this.createAllDataList(units, positions))
              observer.complete()
            })
        })
    })
  }

  updateAllDataList(data: UnitAllData[], positions: Position[]): UnitAllData[] {
    if (!positions) {
      return
    }
    for (const item of data) {
      const position = positions.filter(p => p.unitId === item.unit.unitId)[0]
      item.position = position ? position : item.position
      if (item.position && !item.position.status) {
        this.setPositionStatus(item.position)
      }
    }

    return data
  }

  getUnits(unitGroupIds: number[]): Observable<Unit[]> {
    return this._unitService.getUnitsByGroup(unitGroupIds)
  }

  getUnitsByIds(unitIds: number[]): Observable<Unit[]> {
    return this._unitsFacade.getUnitsByIds$(unitIds)
  }

  createAllDataList(units: Unit[], positions: Position[]): UnitAllData[] {
    if (!positions) {
      return
    }
    const unitsAllData = []
    if (!units) {
      return unitsAllData
    }
    for (const unit of units) {
      const unitData = {
        unit: unit,
        position: positions.filter(p => p.unitId === unit.unitId)[0],
      }
      if (unitData.position && !unitData.position.status) {
        this.setPositionStatus(unitData.position)
      }
      unitsAllData.push(unitData)
    }
    return unitsAllData
  }

  updateUnitsEvents(events: UnitEvent[], units: UnitAllData[]) {
    if (!units) {
      return
    }

    for (const event of events) {
      for (const unit of units) {
        if (event.unitId === unit.unit.unitId) {
          event.unitMake = unit.unit ? `${unit.unit.make || ''} ${unit.unit.model || ''}`.trim() : ''
          event.mileage = unit.position ? unit.position.distance : null
          event.unitName = unit.unit.unitName
        }
      }
    }
  }

  updateUnitsEvent(event: UnitEvent, units: UnitAllData[]) {
    if (!units || !event) {
      return
    }
    for (const unit of units) {
      if (event.unitId === unit.unit.unitId) {
        event.unitMake = unit.unit ? `${unit.unit.make || ''} ${unit.unit.model || ''}`.trim() : ''
        event.mileage = unit.position ? unit.position.distance : null
        event.unitName = unit.unit.unitName
      }
    }
  }

  getGeocoderData(units: UnitAllData[]) {
    const requestData = this.prepareGeocoderData(units)
    if (!requestData) {
      return
    }
    for (const req of requestData) {
      if (req && req.length > 0) {
        this._geocoderService
          .getGeocoder(req)
          .pipe(first())
          .subscribe((data: any[]) => {
            data.forEach(x => {
              const unit = units.find(item => item.unit.unitId === x.id)
              unit.address = this.utils.setAddress(x)
            })
          })
      }
    }
  }

  private prepareGeocoderData(units: UnitAllData[]) {
    const geocoderRequestData: GeocoderReq[] = []
    for (const item of units) {
      if (!item.unit || !item.position) {
        continue
      }
      const data: GeocoderReq = new GeocoderReq()
      data.id = item.unit.unitId
      data.latitude = item.position.position.latitude
      data.longitude = item.position.position.longitude
      data.languageCode = 'pl'

      geocoderRequestData.push(data)
    }

    return this.utils.partDataGeocoder(geocoderRequestData)
  }

  private getPositions(unitIds: number[]): Observable<Position[]> {
    return this._positionService.getPositions(unitIds)
  }

  private getUnitsIds(units: Unit[]): number[] {
    const unitIds = []
    for (const unit of units) {
      unitIds.push(unit.unitId)
    }
    return unitIds
  }

  setPositionStatus(position: Position) {
    if (!position) {
      return
    }

    if ((position.ignition || position.speed > 2) && moment(position.date).isAfter(moment().subtract(5, 'minute'))) {
      position.status = PositionStatus.MOVING
    } else if (!position.ignition && moment(position.date).isAfter(moment().subtract(65, 'minute'))) {
      position.status = PositionStatus.PARKING
    } else {
      position.status = PositionStatus.INACTIVE
    }
  }
}
